import { instantiateShadowRoot } from 'src/utils/widget/shadowRootHelper';
import { BilberryCustomElementBase } from './BilberryCustomElementBase';
import { getYesAttribute } from '@repo/widget-utils/attributes/attribute-helper';

// Generate a zoom-value to counteract the zoom-effect from websites that use
// a <meta name="viewport" content="width=320,...> tag on mobile/small screens.
// The result is that our widgets look bigger than they should, leading to much
// less screen-space, on mobile/small screens. However with this counteracting
// zoom, widgets instead look as they should.
//
// Specifically this happens for wix-websites that use our widgets, but this
// code is generic enough to also apply to other sites that may also do the
// same thing. This is kind of a ugly hack, since all website content is already
// enlarged by the meta-tag, including our widgets. So there is the question if
// we should be doing this in the first place. There is no way for wix-users to
// avoid the viewport tag, it is forced for mobil/small screens.
//
// If no viewport-meta-tag with a number width-value is found, return undefined
// to avoid applying any zoom effect.
function getRootZoomIfMetaWidthIsNumber(): number | undefined {
    const metaViewportContent =
        document.querySelector<HTMLMetaElement>('meta[name="viewport"]')?.content;

    const widthString = metaViewportContent?.match(/width=([0-9]*)/)?.[1];
    const width = widthString ? parseInt(widthString) : null;
    const windowWidth = window.screen.width;

    if (!width) return undefined;

    return width / windowWidth;
}

function getHostStyleString(zoom?: number) {
    return `
        :host {
            display: block;
            max-width: 100%;
            ${zoom ? `zoom: ${zoom};` : ''}
        }
    `;
}

export default class BilberryCustomElement extends BilberryCustomElementBase {
    protected _shadowRoot: ShadowRoot;
    protected _mountPoint: HTMLDivElement;
    protected _popoverMountPoint: HTMLDivElement;
    private _observer: IntersectionObserver | null = null;
    private _isLazyLoaded = false;

    constructor() {
        super();
        const { mountPoint, shadowRoot, popoverMountPoint } = instantiateShadowRoot(this);
        this._shadowRoot = shadowRoot;
        this._mountPoint = mountPoint;
        this._popoverMountPoint = popoverMountPoint;

        const style = document.createElement('style');
        style.textContent = getHostStyleString(getRootZoomIfMetaWidthIsNumber());
        this._shadowRoot.appendChild(style);

        window.addEventListener('resize', () => {
            style.textContent = getHostStyleString(getRootZoomIfMetaWidthIsNumber());
        });
    }

    protected connectedCallback(): void {
        super.connectedCallback();

        const shouldLazyLoad = getYesAttribute(this, 'lazy-load', 'no');

        if (shouldLazyLoad) {
            this._observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting && !this._isLazyLoaded) {
                        this._isLazyLoaded = true;
                        this.renderWidget();
                    }
                },
                { rootMargin: '50px' },
            );
            this._observer.observe(this);
        } else {
            this.renderWidget();
        }
    }

    disconnectedCallback(): void {
        if (this._observer) {
            this._observer.disconnect();
            this._observer = null;
        }
    }

    protected renderWidget(): void {
        // implemented by child classes
    }
}
